export interface EdgeNetInfoInterface {
  Name?: string;
  Up?: boolean;
  Mac?: string;
  Ipv4?: string;
  Ipv6?: string;
  SpeedMbps?: number;
}

export interface EdgeNetInfo {
  LocalIpv4?: string;
  localIpv4?: string;
  LocalIpv6?: string;
  localIpv6?: string;
  Gateway?: string;
  gateway?: string;
  DnsServers?: string[];
  dnsServers?: string[];
  Mac?: string;
  mac?: string;
  WifiSsid?: string;
  wifiSsid?: string;
  WifiRssi?: number;
  wifiRssi?: number;
  LinkSpeedMbps?: number;
  linkSpeedMbps?: number;
  Hostname?: string;
  hostname?: string;
  Interfaces?: EdgeNetInfoInterface[];
  interfaces?: EdgeNetInfoInterface[];
  PublicIp?: string;
  publicIp?: string;
}

export type EdgeStatus = 'online' | 'offline' | 'unknown';

export interface GatePassage {
  edgeId: string;
  logicalDirection: number;
  physicalDirection: number;
  passageCompleted: boolean;
  isTriggeredByRelease: boolean;
  releaseRequestId: string | null;
  ts: string | number;
  payload: {
    logicalDirection: number;
    physicalDirection: number;
    releaseRequestId: string | null;
    isTriggeredByRelease: boolean;
    passageCompleted: boolean;
    payloadType: number;
    version: string | null;
    requestId: string;
  };
}

export interface EdgeItem {
  EdgeId: string;
  Status: EdgeStatus;
  LastTs: string;
  Net?: EdgeNetInfo;
  ApiKeyKind?: 0 | 1;
  LastGatePassage?: GatePassage;
}

export interface IpifyResponse {
  ip: string;
}

export interface EdgeDeviceResponse {
  edgeId?: string;
  EdgeId?: string;
  apiKeyKind?: 0 | 1;
  ApiKeyKind?: 0 | 1;
}

export type HubConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export type EdgeProvisioningStateEnum = 'Pending' | 'Provisioned' | 'Failed' | string;

export interface Equipment {
  EquipmentId: string;
  TenantId: string;
  EquipmentName: string;
  EquipmentLocation: string | null;
  EquipmentPhysicalSerial: string | null;
  EquipmentIsActive: boolean;
  EdgeDeviceId: string;
  EdgeId: string;
  EdgeSwVersion: string;
  EdgeLastSeenUtc: string;
  EdgeIsActive: boolean;
  EdgeProvisioningStateEnum: EdgeProvisioningStateEnum;
  EquipmentAddedOnUtc: string;
  EquipmentUpdatedOnUtc: string | null;
  AccessPointCount: number;
}

export interface ODataEquipmentResponse {
  '@odata.context': string;
  value: Equipment[];
}

export enum DirectionControlEnum {
  Blocked = 0,
  Free = 1,
  Controlled = 2,
}

export enum PassagePhysicalDirectionEnum {
  Clockwise = 0,
  Counterclockwise = 1,
  SideAtoB = 0, // Alias for Clockwise
  SideBtoA = 1, // Alias for Counterclockwise
}

export enum BoardTypeEnum {
  LiteNet2 = 1,
  LiteNet3 = 2,
  Other = 9,
}

export enum AccessPointBackendTypeEnum {
  LocalBoard = 0,
  ExternalController = 1,
}

/**
 * Converts enum values from OData response (which may come as string keys) to numeric values
 * @param enumObj - The enum object to use for conversion
 * @param value - The value to convert (can be string key or number)
 * @returns The numeric value of the enum, or the original value if conversion fails
 */
export function convertEnumToNumber<T extends Record<string, string | number>>(
  enumObj: T,
  value: string | number | undefined | null
): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (!isNaN(numericValue) && isFinite(numericValue)) {
      return numericValue;
    }

    const enumValue = enumObj[value as keyof T];
    if (typeof enumValue === 'number') {
      return enumValue;
    }
  }

  return typeof value === 'number' ? value : Number(value);
}

export interface AccessPoint {
  AccessPointId: string;
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  BackendTypeEnum: number;
  CameraDeviceId: string | null;
  EntryMode: number; // DirectionControlEnum
  ExitMode: number; // DirectionControlEnum
  EntryDirection: number; // PassagePhysicalDirectionEnum
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  ReleaseDurationSeconds: number;
  GateNumber: number | null;
  EdgeId: string; // derived from equipment
  Status: EdgeStatus; // 'online' | 'offline' | 'unknown'
  LastTs: string;
  CameraDevice?: TCameraDevice;
}

export interface CreateAccessPointRequest {
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  BackendTypeEnum: number;
  CameraDeviceId: string | null;
  CameraDevice: TCameraDevice | null;
  EntryMode: number;
  ExitMode: number;
  EntryDirection: number;
  ReleaseDurationSeconds: number;
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
}

export interface UpdateAccessPointRequest {
  AccessPointId: string;
  EquipmentId?: string | null;
  Name?: string;
  IsActive?: boolean;
  BackendTypeEnum?: number;
  CameraDeviceId?: string | null;
  EntryMode?: number;
  ExitMode?: number;
  EntryDirection?: number;
  ReleaseDurationSeconds?: number;
  IsSplitScreenMode?: boolean;
  ShowAdvertisement?: boolean;
  CameraDevice?: TCameraDevice;
}

export interface EdgeConfigurationDeployment {
  EdgeConfigurationDeploymentId?: string;
  EdgeId?: string;
  EquipmentId?: string;
  IsActive?: boolean;
  LastSeenUtc?: string;
  [key: string]: any; // Allow additional properties from API
}

export interface AccessPointResponse {
  AccessPointId: string;
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  BackendTypeEnum: number | string; // OData may return enum as string key
  CameraDeviceId: string | null;
  EntryMode: number | string; // OData may return enum as string key
  ExitMode: number | string; // OData may return enum as string key
  EntryDirection: number | string; // OData may return enum as string key
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  ReleaseDurationSeconds: number;
  GateNumber: number | null;
  EdgeConfigurationDeployment?: EdgeConfigurationDeployment | null;
  CameraDevice?: TCameraDevice | null;
  [key: string]: any; // Allow additional properties from API
}

export interface ODataAccessPointResponse {
  '@odata.context': string;
  value: AccessPointResponse[];
}

export enum ELogicalDirectionEnum {
  Entry = 1,
  Exit = 2,
  Both = 3,
}

export enum AccessEvalStatus {
  Allowed = 'Allowed',
  Denied = 'Denied',
  NotIdentified = 'NotIdentified',
  Error = 'Error',
}

export interface AccessEvalResult {
  edgeId: string;
  status: AccessEvalStatus;
  isAllowed: boolean;
  logicalDirection: string;
  personId: string;
  name: string | null;
  message: string | null;
  effectiveUntilUtc: string;
  logs: any;
  error?: string | null;
  requestId: string;
  ts: string;
}

export enum ECamType {
  Other,
  Webcam,
  IdFace,
  Intelbras,
  Embedded,
}

export enum AccessMethodEnum {
  Bluetooth = 0,
  Card = 1,
  Face = 2,
  Fingerprint = 3,
  Keyboard = 4,
  PersonId = 5,
  QRCode = 6,
  F2 = 7,
  F3 = 8,
}

export enum AccessIntentEnum {
  Unknown = 0,
  Entering = 1,
  Exiting = 2,
}

export enum CodeIdentifierEnum {
  PersonCode = 0,
  AccessPin = 1,
}

export interface EvaluateByPersonIdRequest {
  Identifier: string;
  AccessMethod: AccessMethodEnum;
  AccessIntent: AccessIntentEnum;
}

export type TCameraDevice = {
  CameraDeviceId?: string;
  EquipmentId?: string;
  Name?: string;
  IsActive?: boolean;
  Type?: ECamType;
  Ip?: string | null;
  Login?: string | null;
  Password?: string | null;
  RtspPort?: number | null;
  TenantId?: string;
  AddedBy?: string;
  AddedOnUtc?: string;
  UpdatedBy?: string;
  UpdatedOnUtc?: string;
  DeletedBy?: string | null;
  DeletedOnUtc?: string | null;
  Index?: number;
  InstanceId?: string;
};